import axios from 'axios';

// API Base URL - must be set via environment variable
const API_BASE_URL = process.env.REACT_APP_PWA_API_URL;

if (!API_BASE_URL) {
  throw new Error('REACT_APP_PWA_API_URL environment variable is not set');
}

// Axios instance with credentials enabled (for session cookies)
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Critical for session-based auth
  headers: {
    'Content-Type': 'application/json',
  },
});

// API Service
const apiService = {
  // ============ AUTH ENDPOINTS ============
  
  // Get current authenticated user + entitlements
  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/auth/user');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        return null; // Not authenticated
      }
      throw error;
    }
  },

  // Login with email/password
  login: async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    return response.data;
  },

  // Signup with email/password
  signup: async (email, password, name) => {
    const response = await api.post('/api/auth/signup', { email, password, name });
    return response.data;
  },

  // Logout
  logout: async () => {
    const response = await api.post('/api/auth/logout');
    return response.data;
  },

  // OAuth login (Google, Apple, Facebook)
  getOAuthUrl: (provider) => {
    return `${API_BASE_URL}/api/auth/${provider}`;
  },

  // ============ SUBSCRIPTION ENDPOINTS ============
  
  // Create Stripe checkout session
  createCheckoutSession: async (planType) => {
    const response = await api.post('/api/stripe/create-checkout', {
      plan_type: planType,
    });
    return response.data;
  },

  // Get subscription status
  getSubscription: async () => {
    const response = await api.get('/api/user/subscription');
    return response.data;
  },

  // Cancel subscription
  cancelSubscription: async () => {
    const response = await api.post('/api/user/cancel-subscription');
    return response.data;
  },

  // Get billing portal URL
  getBillingPortal: async () => {
    const response = await api.post('/api/stripe/billing-portal');
    return response.data;
  },

  // ============ USER PROFILE ENDPOINTS ============
  
  // Get user profile
  getUserProfile: async () => {
    const response = await api.get('/api/user/profile');
    return response.data;
  },

  // Update user profile
  updateUserProfile: async (profileData) => {
    const response = await api.patch('/api/user/profile', profileData);
    return response.data;
  },

  // ============ PROGRESS ENDPOINTS ============
  
  // Get daily progress
  getDailyProgress: async (date) => {
    const response = await api.get('/api/progress/daily', {
      params: { date },
    });
    return response.data;
  },

  // Get training plan
  getTrainingPlan: async () => {
    const response = await api.get('/api/plans/training');
    return response.data;
  },

  // Get nutrition plan
  getNutritionPlan: async () => {
    const response = await api.get('/api/plans/nutrition');
    return response.data;
  },

  // ============ FUNNEL HANDOFF ============
  // Best-effort push of the website funnel onboarding + generated plan to the
  // external PWA backend so the user doesn't have to redo onboarding inside the
  // app. The Replit backend either ingests these fields on /api/user/profile
  // or ignores unknown fields gracefully.
  pushFunnelData: async ({ answers, plan, planId }) => {
    try {
      const response = await api.patch('/api/user/profile', {
        onboarding: answers,
        training_plan: plan,
        funnel_plan_id: planId,
        source: 'website_funnel',
      });
      return { ok: true, data: response.data };
    } catch (error) {
      // Don't crash signup if the PWA backend is unreachable or rejects
      // unknown fields. The plan still lives on our funnel backend and can be
      // retried later.
      return { ok: false, error: error?.response?.data || error.message };
    }
  },
};

export default apiService;
